import { useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { formatUsdcBaseUnits } from "@hybrid-escrow/shared";
import { encodeWithdrawCall } from "@hybrid-escrow/chain";
import { formatEther } from "viem";
import { useAuthPrincipal } from "../hooks/useAuthPrincipal";
import { useWalletBalances } from "../hooks/useWalletBalances";
import { useWithdrawableBalance } from "../hooks/useWithdrawableBalance";
import { usePublicConfig, useNetworkCheck } from "../hooks/usePublicConfig";
import { useEscrowAction } from "../lib/coinbase/useEscrowAction";
import { apiClient } from "../lib/api-client";
import { env } from "../lib/env";
import { CopyButton } from "../components/CopyButton";
import { ExplorerAddressLink } from "../components/ExplorerLink";
import { LoadingState, describeError } from "../components/States";
import { cdpNetworkForChainId, chainNameForChainId } from "../lib/coinbase/network";

export function WalletPage() {
  const { isInitialized, isSignedIn, smartAccountAddress, eoaAddress, email, signOut } =
    useAuthPrincipal();
  const { data: config } = usePublicConfig();
  const network = useNetworkCheck();
  const address = smartAccountAddress ?? eoaAddress;
  const { data: balances, isLoading: balancesLoading } = useWalletBalances(address);
  const { data: withdrawableBaseUnits } = useWithdrawableBalance(smartAccountAddress);
  const escrowAction = useEscrowAction();
  const queryClient = useQueryClient();
  const cdpNetwork = config ? cdpNetworkForChainId(config.chainId) : null;

  const onrampMutation = useMutation({
    mutationFn: () => apiClient.createOnrampSession(),
    onSuccess: (session) => {
      // New tab, same as any other "leave the app to pay somewhere else" link — the returned
      // URL is single-use, so this is the one and only time it's opened.
      window.open(session.onrampUrl, "_blank", "noopener,noreferrer");
    },
  });

  useEffect(() => {
    if (escrowAction.phase === "confirmed") {
      void queryClient.invalidateQueries({ queryKey: ["withdrawable-balance"] });
      void queryClient.invalidateQueries({ queryKey: ["wallet-balances"] });
    }
  }, [escrowAction.phase, queryClient]);

  async function handleWithdraw() {
    if (!smartAccountAddress || !config || !cdpNetwork) return;
    const call = encodeWithdrawCall(config.escrowContractAddress, smartAccountAddress);
    await escrowAction.submitRaw({
      smartAccountAddress,
      network: cdpNetwork,
      to: call.to,
      data: call.data,
      value: call.value,
    });
  }

  if (!isInitialized) return <LoadingState label="Loading wallet…" />;
  if (!isSignedIn) {
    return (
      <div className="empty-state">
        <h1>Wallet</h1>
        <p>Sign in to view your wallet.</p>
      </div>
    );
  }

  return (
    <div className="stack" style={{ maxWidth: 560 }}>
      <h1>Wallet</h1>

      <div className="card stack">
        <div>
          <p className="text-sm muted">Signed in as</p>
          <p>{email ?? "—"}</p>
        </div>

        <div>
          <p className="text-sm muted">Smart Account address</p>
          {address ? (
            <>
              <div className="row">
                <ExplorerAddressLink
                  explorerUrl={config?.explorerUrl ?? ""}
                  address={address}
                  label={address}
                />
                <CopyButton value={address} label="Copy address" />
              </div>
              <p className="text-sm muted">
                To fund this wallet, send USDC directly to this address on{" "}
                {config ? chainNameForChainId(config.chainId) : "the network shown above"} from any
                exchange or wallet you control.
              </p>
            </>
          ) : (
            <p className="muted">
              Your Smart Account is still being provisioned. Try refreshing shortly.
            </p>
          )}
        </div>

        <div>
          <p className="text-sm muted">Network</p>
          {config ? (
            <span className={network.matches ? "badge badge--success" : "badge badge--danger"}>
              {chainNameForChainId(config.chainId)}
            </span>
          ) : (
            <span className="muted">Unknown</span>
          )}
        </div>

        <div>
          <p className="text-sm muted">USDC balance</p>
          {balancesLoading ? (
            <p className="muted">Loading…</p>
          ) : (
            <p className="money">
              ${balances ? formatUsdcBaseUnits(balances.usdcBaseUnits) : "0.00"}
            </p>
          )}
        </div>

        <div>
          <p className="text-sm muted">ETH balance (for gas)</p>
          {balancesLoading ? (
            <p className="muted">Loading…</p>
          ) : (
            <p>{balances ? formatEther(balances.ethWeiForGas) : "0"} ETH</p>
          )}
          <p className="text-sm muted">
            {env.VITE_USE_CDP_PAYMASTER === "true"
              ? "Purchases are gas-sponsored via the Coinbase Paymaster; this balance only matters if sponsorship isn't available for a given transaction."
              : "Transactions are paid for in ETH on Base. A tiny amount (a few cents) covers many purchases."}
          </p>
        </div>

        {address && cdpNetwork === "base" && (
          <>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={onrampMutation.isPending}
              onClick={() => onrampMutation.mutate()}
            >
              {onrampMutation.isPending ? "Preparing checkout…" : "Buy USDC with Coinbase"}
            </button>
            {onrampMutation.isError && (
              <p className="field-error-text" role="alert">
                {describeError(onrampMutation.error)}
              </p>
            )}
          </>
        )}
        {address && cdpNetwork === "base-sepolia" && (
          <p className="text-sm muted">
            Testnet mode ({chainNameForChainId(config?.chainId ?? 0)}): USDC can&apos;t be purchased
            with real money here. Use a Base Sepolia USDC faucet to fund this address for testing.
          </p>
        )}

        <button type="button" className="btn btn-secondary" onClick={() => void signOut()}>
          Sign out
        </button>
      </div>

      {Boolean(withdrawableBaseUnits) && (
        <div className="card stack">
          <div className="alert alert--warning" role="alert">
            <strong>You have ${formatUsdcBaseUnits(withdrawableBaseUnits!)} waiting to be paid out.</strong>
            <p>
              A prior payout to this address couldn&apos;t be completed automatically (for example,
              your address was temporarily blocked by USDC&apos;s issuer at that moment) and was
              credited here instead. This is the only way to claim it — nothing pays it out on its
              own. Anyone can trigger this payment, but the funds always land on this address.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!smartAccountAddress || !cdpNetwork || escrowAction.phase !== "idle"}
            onClick={() => void handleWithdraw()}
          >
            {escrowAction.phase === "awaiting-signature"
              ? "Confirm in wallet…"
              : `Withdraw $${formatUsdcBaseUnits(withdrawableBaseUnits!)}`}
          </button>
          {escrowAction.phase === "submitted" && (
            <p className="text-sm muted" role="status" aria-live="polite">
              Transaction submitted, waiting for onchain confirmation…
            </p>
          )}
          {escrowAction.phase === "confirmed" && (
            <p className="alert alert--success" role="status">
              Confirmed onchain. Balance will update shortly.
            </p>
          )}
          {escrowAction.phase === "failed" && escrowAction.error && (
            <div className="alert alert--danger" role="alert">
              <strong>The transaction didn&apos;t go through.</strong>
              <p>{escrowAction.error}</p>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={escrowAction.reset}
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
